import { ErrorMessage, Formik } from "formik";
import { LoginTemplate } from "../../template/login-template";
import {
  PrimaryButton,
  Spinner,
  SuccessBanner,
} from "@graffer-inc/console-design-kit";
import { parseQueryOfLocation } from "../../../utils/url-utils";
import { useLocation } from "react-router-dom";
import { useRef, useState } from "react";
import apiClient from "../../../application/apis/tenant-management/tenant-management-client";
import { reportUnexpectedError } from "../../../error-reporter";
import { CloudGovApiError } from "../../../application/domain/api-error";

export const RequestPasswordResetPage = () => {
  const { slug: initialSlug } = parseQueryOfLocation(useLocation()) as {
    slug: string;
  };

  const slugInput = useRef<HTMLInputElement>(null);
  const accountNameInput = useRef<HTMLInputElement>(null);
  const [error, setError] = useState("");
  const [completed, setCompleted] = useState(false);

  return (
    <LoginTemplate>
      <div className="m-4">
        <h1 className="title is-size-4 has-text-centered">
          仮パスワードの再設定
        </h1>
      </div>
      {completed && (
        <SuccessBanner
          message="アカウントに設定されているメールアドレスに仮パスワード再設定用の案内メールを送信しました。"
          description="アカウントにメールアドレスの設定が無い場合は送信されません。管理者にパスワードリセットを依頼してください。"
        />
      )}
      {!!error && (
        <div className="notification is-danger is-light" style={{}}>
          {error}
        </div>
      )}
      <Formik
        initialValues={{
          slug: initialSlug ? initialSlug : "",
          accountName: "",
        }}
        validate={(values) => {
          const errors: any = {};
          if (!values.slug) {
            errors.slug = "組織コードは必須です。";
          }
          if (!values.accountName) {
            errors.accountName = "アカウント名は必須です。";
          }
          return errors;
        }}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            setError("");
            setSubmitting(true);

            await apiClient.requestTmpPasswordReset(values);

            setCompleted(true);
          } catch (e) {
            if (
              CloudGovApiError.isErrorCode(e, "org_user_has_not_email") ||
              CloudGovApiError.isErrorCode(
                e,
                "org_user_with_same_email_already_exists",
              ) ||
              CloudGovApiError.is404(e)
            ) {
              setError(
                "仮パスワードの再設定メールを送信できませんでした。" +
                  "アカウントが存在しない場合、メールアドレスの設定がない場合またはテナント内でメールアドレスが重複している場合は送信できません。",
              );
              return;
            }

            setError("予期しないエラーが発生しました。");
            reportUnexpectedError(e);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          isValid,
        }) => (
          <form onSubmit={handleSubmit}>
            <div className="field">
              <label className="label" htmlFor="slug">
                組織コード
              </label>
              <div className="control">
                <input
                  data-for-rpa-id="slug"
                  name="slug"
                  className="input"
                  placeholder={"組織コード (例 city-graffer)"}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.slug}
                  disabled={!!initialSlug && initialSlug.length > 0}
                  ref={slugInput}
                />
              </div>
              <ErrorMessage name="slug">
                {(msg: string) => <p className="help is-danger">{msg}</p>}
              </ErrorMessage>
            </div>
            <div className="field">
              <label className="label" htmlFor="accountName">
                アカウント名
              </label>
              <input
                name="accountName"
                className="input"
                data-for-rpa-id="account"
                placeholder={"アカウント名（英数字記号）"}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.accountName}
                ref={accountNameInput}
              />
              <ErrorMessage name="accountName">
                {(msg: string) => <p className="help is-danger">{msg}</p>}
              </ErrorMessage>
            </div>
            <div className={"has-text-centered"}>
              <PrimaryButton
                text={"再設定用のメールを送信する"}
                disabled={isSubmitting || !isValid}
                type="submit"
              />
            </div>
            <Spinner loading={isSubmitting} />
          </form>
        )}
      </Formik>
    </LoginTemplate>
  );
};
